import {
  Avatar,
  Button,
  Hamburger,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  NavDrawer,
  NavDrawerBody,
  NavItem,
  Tooltip,
  tokens,
} from "@fluentui/react-components"
import {
  bundleIcon,
  type FluentIcon,
  Globe20Filled,
  Globe20Regular,
  History20Filled,
  History20Regular,
  Home20Filled,
  Home20Regular,
  KeyMultiple20Filled,
  KeyMultiple20Regular,
  People20Filled,
  People20Regular,
  Person20Regular,
  PersonTag20Filled,
  PersonTag20Regular,
  PlugConnected20Filled,
  PlugConnected20Regular,
  Server20Filled,
  Server20Regular,
  Settings20Filled,
  Settings20Regular,
  SignOut20Regular,
  WeatherMoon20Regular,
  WeatherSunny20Regular,
} from "@fluentui/react-icons"
import type { ScopeName } from "@rema/shared"
import { useState } from "react"
import { Outlet, useLocation, useNavigate } from "react-router"
import { useSession } from "../auth/session"
import { es } from "../i18n/es"
import { useTheme } from "../theme/theme-provider"
import remafleetLogo from "./logo-remafleet.png"

const DashboardIcon = bundleIcon(Home20Filled, Home20Regular)
const UsersIcon = bundleIcon(People20Filled, People20Regular)
const DomainsIcon = bundleIcon(Globe20Filled, Globe20Regular)
const RolesIcon = bundleIcon(PersonTag20Filled, PersonTag20Regular)
const ScopesIcon = bundleIcon(KeyMultiple20Filled, KeyMultiple20Regular)
const NodesIcon = bundleIcon(Server20Filled, Server20Regular)
const ClientsIcon = bundleIcon(PlugConnected20Filled, PlugConnected20Regular)
const AuditIcon = bundleIcon(History20Filled, History20Regular)
const SettingsIcon = bundleIcon(Settings20Filled, Settings20Regular)

interface NavEntry {
  path: string
  label: string
  icon: FluentIcon
  // Scope mínimo para ver la sección; la autorización real la impone la API
  scope: ScopeName | null
}

const NAV_ENTRIES: NavEntry[] = [
  { path: "/", label: es.nav.dashboard, icon: DashboardIcon, scope: "metrics:read" },
  { path: "/users", label: es.nav.users, icon: UsersIcon, scope: "users:read" },
  { path: "/domains", label: es.nav.domains, icon: DomainsIcon, scope: "domains:read" },
  { path: "/roles", label: es.nav.roles, icon: RolesIcon, scope: "roles:read" },
  { path: "/scopes", label: es.nav.scopes, icon: ScopesIcon, scope: "scopes:read" },
  { path: "/nodes", label: es.nav.nodes, icon: NodesIcon, scope: "nodes:read" },
  { path: "/clients", label: es.nav.clients, icon: ClientsIcon, scope: "clients:read" },
  { path: "/audit", label: es.nav.audit, icon: AuditIcon, scope: "audit:read" },
  { path: "/settings", label: es.nav.settings, icon: SettingsIcon, scope: "settings:read" },
]

const useStyles = makeStyles({
  root: {
    display: "grid",
    gridTemplateRows: "48px 1fr",
    height: "100%",
  },
  // Topbar oscura con la identidad de RemaFleet (mismo look que el panel del
  // gateway: fondo #1a1a1a, texto claro, sin importar el tema claro/oscuro).
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    backgroundColor: "#1a1a1a",
    color: "#e0e0e0",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginRight: "auto",
  },
  logo: {
    height: "20px",
    display: "block",
  },
  separator: {
    width: "1px",
    height: "20px",
    backgroundColor: "rgba(255,255,255,0.3)",
  },
  moduleName: {
    color: "#e0e0e0",
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightRegular,
  },
  onBrandButton: {
    color: "#e0e0e0",
    ":hover": {
      color: "#ffffff",
      backgroundColor: "rgba(255,255,255,0.08)",
    },
    ":hover:active": {
      color: "#ffffff",
      backgroundColor: "rgba(255,255,255,0.13)",
    },
  },
  content: {
    display: "flex",
    minHeight: 0,
  },
  // La barra lateral no debe encogerse: sin esto, una página con contenido ancho
  // (p. ej. la pestaña Webhooks de Ajustes) hace que flex aplaste el NavDrawer a
  // ~1px. main ya tiene minWidth:0 + overflow:auto, así que absorbe el exceso.
  //
  // Sidebar oscura igual que el panel del gateway: fondo #1a1a1a, ítems con
  // texto claro, hover translúcido y acento verde (#4caf50) en el seleccionado.
  nav: {
    flexShrink: 0,
    "&.fui-NavDrawer": {
      backgroundColor: "#1a1a1a",
      borderRight: "1px solid #333",
    },
    "& .fui-NavDrawerBody": {
      backgroundColor: "#1a1a1a",
    },
    "& .fui-NavItem": {
      color: "#e0e0e0",
    },
    "& .fui-NavItem__icon": {
      color: "#e0e0e0",
    },
    "& .fui-NavItem:hover": {
      backgroundColor: "rgba(255,255,255,0.08)",
      color: "#ffffff",
    },
    "& .fui-NavItem[aria-current]": {
      backgroundColor: "rgba(255,255,255,0.08)",
      color: "#ffffff",
    },
    // Barra indicadora del ítem activo → verde RemaFleet.
    "& .fui-NavItem::after": {
      backgroundColor: "#4caf50",
    },
  },
  main: {
    flexGrow: 1,
    minWidth: 0,
    overflow: "auto",
    padding: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground2,
  },
})

// Valor de navegación activo: primer segmento de la ruta ("/users/123" → "/users")
const selectedNavValue = (pathname: string): string => {
  if (pathname === "/") return "/"
  const segment = pathname.split("/")[1]
  return segment ? `/${segment}` : "/"
}

export const AppShell = () => {
  const styles = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, hasScope, logout } = useSession()
  const { mode, toggleMode } = useTheme()
  const [navOpen, setNavOpen] = useState(true)

  const visibleEntries = NAV_ENTRIES.filter(
    (entry) => entry.scope === null || hasScope(entry.scope),
  )

  const handleLogout = async () => {
    await logout()
    navigate("/login", { replace: true })
  }

  const themeLabel = mode === "light" ? es.shell.themeToDark : es.shell.themeToLight

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <Hamburger
          aria-label={es.shell.toggleNav}
          className={styles.onBrandButton}
          onClick={() => setNavOpen((open) => !open)}
        />
        <div className={styles.brand}>
          <img src={remafleetLogo} alt="RemaFleet" className={styles.logo} />
          <span className={styles.separator} />
          <span className={styles.moduleName}>Auth</span>
        </div>
        <Tooltip content={themeLabel} relationship="label">
          <Button
            appearance="transparent"
            className={styles.onBrandButton}
            icon={mode === "light" ? <WeatherMoon20Regular /> : <WeatherSunny20Regular />}
            onClick={toggleMode}
          />
        </Tooltip>
        <Menu positioning="below-end">
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="transparent"
              className={styles.onBrandButton}
              aria-label={es.shell.userMenu}
              icon={
                <Avatar
                  size={28}
                  color="colorful"
                  name={user?.name ?? user?.username ?? undefined}
                />
              }
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem icon={<Person20Regular />} onClick={() => navigate("/profile")}>
                {es.shell.profile}
              </MenuItem>
              <MenuDivider />
              <MenuItem icon={<SignOut20Regular />} onClick={() => void handleLogout()}>
                {es.shell.logout}
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </header>
      <div className={styles.content}>
        <NavDrawer
          className={styles.nav}
          type="inline"
          open={navOpen}
          selectedValue={selectedNavValue(location.pathname)}
          onNavItemSelect={(_, data) => navigate(data.value)}
        >
          <NavDrawerBody>
            {visibleEntries.map((entry) => (
              <NavItem key={entry.path} value={entry.path} icon={<entry.icon />}>
                {entry.label}
              </NavItem>
            ))}
          </NavDrawerBody>
        </NavDrawer>
        <main className={styles.main}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}
